/**
 * UNSBrowser Component
 *
 * Browse unified namespace (factory hierarchy) to select datapoints for dataset creation.
 * Displays areas, lines, stations with their mapped datapoints as a selectable tree.
 */

import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Input } from "@/shared/ui/input";
import { Checkbox } from "@/shared/ui/checkbox";
import { Badge } from "@/shared/ui/badge";
import { cn } from "@/shared/lib/utils";
import {
  ChevronDown,
  ChevronRight,
  Layers,
  Cpu,
  Search,
  GitBranch,
  Database,
} from "lucide-react";
import type { SelectedDatapoint } from "../../types/dataset";

// =============================================================================
// Types
// =============================================================================

interface Area {
  id: string;
  name: string;
  description: string | null;
  color: string;
  sort_order: number;
}

interface Line {
  id: string;
  area_id: string;
  name: string;
  description: string | null;
  sort_order: number;
}

interface Station {
  id: string;
  line_id: string;
  name: string;
  description: string | null;
  station_type: string | null;
  sort_order: number;
}

interface FieldMappingWithDatapoint {
  id: string;
  datapoint_id: string;
  factory_id: string;
  area_id: string | null;
  line_id: string | null;
  station_id: string | null;
  is_active: boolean;
  datapoint: {
    id: string;
    display_name: string;
    field_name: string;
    measurement_name: string;
    bucket_name: string;
    data_source_id: string;
    description?: string | null;
    unit?: { symbol: string } | null;
  };
}

interface UNSBrowserProps {
  factoryId: string | null;
  selections: Map<string, SelectedDatapoint>;
  onSelectionChange: (selections: Map<string, SelectedDatapoint>) => void;
  lockedBucket: string | null;
  className?: string;
}

// =============================================================================
// Component
// =============================================================================

export function UNSBrowser({
  factoryId,
  selections,
  onSelectionChange,
  lockedBucket,
  className,
}: UNSBrowserProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set());
  const [expandedLines, setExpandedLines] = useState<Set<string>>(new Set());

  // =============================================================================
  // Queries
  // =============================================================================

  const { data: areas = [] } = useQuery({
    queryKey: ["factory-areas", factoryId],
    queryFn: async () => {
      if (!factoryId) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("factory_areas")
        .select("id, name, description, color, sort_order")
        .eq("factory_id", factoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as Area[];
    },
    enabled: !!factoryId,
  });

  const { data: lines = [] } = useQuery({
    queryKey: ["production-lines", factoryId],
    queryFn: async () => {
      if (!factoryId) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("production_lines")
        .select("id, area_id, name, description, sort_order")
        .eq("factory_id", factoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as Line[];
    },
    enabled: !!factoryId,
  });

  const { data: stations = [] } = useQuery({
    queryKey: ["stations", factoryId],
    queryFn: async () => {
      if (!factoryId) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("stations")
        .select("id, line_id, name, description, station_type, sort_order")
        .eq("factory_id", factoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as Station[];
    },
    enabled: !!factoryId,
  });

  const { data: fieldMappings = [], isLoading } = useQuery({
    queryKey: ["field-mappings", "with-datapoints", factoryId],
    queryFn: async () => {
      if (!factoryId) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("field_mappings")
        .select(
          `
          id,
          datapoint_id,
          factory_id,
          area_id,
          line_id,
          station_id,
          is_active,
          datapoint:datapoints (
            id,
            display_name,
            field_name,
            measurement_name,
            bucket_name,
            data_source_id,
            description,
            unit:units (symbol)
          )
        `
        )
        .eq("factory_id", factoryId)
        .eq("is_active", true);
      if (error) throw error;
      return data as FieldMappingWithDatapoint[];
    },
    enabled: !!factoryId,
  });

  // =============================================================================
  // Helpers
  // =============================================================================

  const getMappingsForElement = (
    type: "factory" | "area" | "line" | "station",
    id: string
  ) => {
    return fieldMappings.filter((m) => {
      if (type === "factory") return !m.area_id && !m.line_id && !m.station_id;
      if (type === "area") return m.area_id === id && !m.line_id && !m.station_id;
      if (type === "line") return m.line_id === id && !m.station_id;
      if (type === "station") return m.station_id === id;
      return false;
    });
  };

  const filteredMappings = useMemo(() => {
    if (!searchQuery.trim()) return fieldMappings;
    const query = searchQuery.toLowerCase();
    return fieldMappings.filter(
      (m) =>
        m.datapoint.display_name.toLowerCase().includes(query) ||
        m.datapoint.field_name.toLowerCase().includes(query) ||
        m.datapoint.measurement_name.toLowerCase().includes(query)
    );
  }, [fieldMappings, searchQuery]);

  const toggleArea = (areaId: string) => {
    setExpandedAreas((prev) => {
      const next = new Set(prev);
      if (next.has(areaId)) next.delete(areaId);
      else next.add(areaId);
      return next;
    });
  };

  const toggleLine = (lineId: string) => {
    setExpandedLines((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const isDatapointDisabled = (mapping: FieldMappingWithDatapoint) => {
    if (!lockedBucket) return false;
    return mapping.datapoint.bucket_name !== lockedBucket;
  };

  const toggleDatapoint = (mapping: FieldMappingWithDatapoint) => {
    if (isDatapointDisabled(mapping)) return;

    const newSelections = new Map(selections);
    if (newSelections.has(mapping.id)) {
      newSelections.delete(mapping.id);
    } else {
      newSelections.set(mapping.id, {
        fieldMappingId: mapping.id,
        datapointId: mapping.datapoint.id,
        displayName: mapping.datapoint.display_name,
        dataSourceId: mapping.datapoint.data_source_id,
        bucketName: mapping.datapoint.bucket_name,
        measurementName: mapping.datapoint.measurement_name,
        fieldName: mapping.datapoint.field_name,
        areaId: mapping.area_id,
        lineId: mapping.line_id,
        stationId: mapping.station_id,
      });
    }
    onSelectionChange(newSelections);
  };

  const toggleAllInGroup = (mappings: FieldMappingWithDatapoint[]) => {
    const selectableMappings = mappings.filter((m) => !isDatapointDisabled(m));
    if (selectableMappings.length === 0) return;

    const newSelections = new Map(selections);
    const allSelected = selectableMappings.every((m) => newSelections.has(m.id));

    if (allSelected) {
      selectableMappings.forEach((m) => newSelections.delete(m.id));
    } else {
      selectableMappings.forEach((m) => {
        newSelections.set(m.id, {
          fieldMappingId: m.id,
          datapointId: m.datapoint.id,
          displayName: m.datapoint.display_name,
          dataSourceId: m.datapoint.data_source_id,
          bucketName: m.datapoint.bucket_name,
          measurementName: m.datapoint.measurement_name,
          fieldName: m.datapoint.field_name,
          areaId: m.area_id,
          lineId: m.line_id,
          stationId: m.station_id,
        });
      });
    }
    onSelectionChange(newSelections);
  };

  // =============================================================================
  // Render helpers
  // =============================================================================

  const renderDatapointItem = (mapping: FieldMappingWithDatapoint) => {
    const isSelected = selections.has(mapping.id);
    const isDisabled = isDatapointDisabled(mapping);

    return (
      <label
        key={mapping.id}
        className={cn(
          "flex items-start gap-3 p-2 rounded-md cursor-pointer hover:bg-muted/50 transition-colors",
          isDisabled && "opacity-50 cursor-not-allowed"
        )}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => toggleDatapoint(mapping)}
          disabled={isDisabled}
          className="mt-0.5"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium truncate">
              {mapping.datapoint.display_name}
            </span>
            {mapping.datapoint.unit?.symbol && (
              <Badge variant="outline" className="text-xs">
                {mapping.datapoint.unit.symbol}
              </Badge>
            )}
            <Badge
              variant={isDisabled ? "secondary" : "outline"}
              className="text-xs"
            >
              {mapping.datapoint.bucket_name}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {mapping.datapoint.measurement_name} · {mapping.datapoint.field_name}
          </p>
        </div>
      </label>
    );
  };

  const renderMappingGroup = (
    mappings: FieldMappingWithDatapoint[],
    label: string
  ) => {
    if (mappings.length === 0) {
      return (
        <p className="text-xs text-muted-foreground py-2">No datapoints mapped</p>
      );
    }

    const selectableMappings = mappings.filter((m) => !isDatapointDisabled(m));
    const allSelected =
      selectableMappings.length > 0 &&
      selectableMappings.every((m) => selections.has(m.id));
    const someSelected =
      selectableMappings.some((m) => selections.has(m.id)) && !allSelected;

    return (
      <div className="space-y-1">
        {selectableMappings.length > 1 && (
          <label className="flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted/50">
            <Checkbox
              checked={allSelected}
              ref={(el) => {
                if (el) (el as HTMLButtonElement).dataset.indeterminate = String(someSelected);
              }}
              onCheckedChange={() => toggleAllInGroup(mappings)}
            />
            <span className="text-xs font-medium text-muted-foreground">
              Select all {label} ({selectableMappings.length})
            </span>
          </label>
        )}
        {mappings.map(renderDatapointItem)}
      </div>
    );
  };

  // =============================================================================
  // Render
  // =============================================================================

  if (!factoryId) {
    return (
      <div className={cn("flex items-center justify-center h-full", className)}>
        <div className="text-center text-muted-foreground">
          <Database className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p>Select a factory to browse datapoints</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn("flex items-center justify-center h-full", className)}>
        <div className="text-center text-muted-foreground">
          <p>Loading datapoints...</p>
        </div>
      </div>
    );
  }

  if (fieldMappings.length === 0) {
    return (
      <div className={cn("flex items-center justify-center h-full", className)}>
        <div className="text-center text-muted-foreground">
          <Layers className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p>No datapoints mapped for this factory</p>
          <p className="text-sm mt-1">
            Use the UNS Mapper to map data sources first
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Search */}
      <div className="p-4 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search datapoints..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        {selections.size > 0 && (
          <p className="text-xs text-muted-foreground mt-2">
            {selections.size} datapoint{selections.size !== 1 ? "s" : ""} selected
          </p>
        )}
      </div>

      {/* Hierarchy Tree */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-3">
          {areas.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Layers className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No factory structure configured</p>
            </div>
          ) : (
            areas.map((area) => {
              const areaLines = lines.filter((l) => l.area_id === area.id);
              const isAreaExpanded = expandedAreas.has(area.id);
              const areaMappings = getMappingsForElement("area", area.id).filter(
                (m) =>
                  !searchQuery ||
                  filteredMappings.some((fm) => fm.id === m.id)
              );

              // Check if area has any matching content when searching
              const hasMatchingContent =
                !searchQuery ||
                areaMappings.length > 0 ||
                areaLines.some((line) => {
                  const lineMappings = getMappingsForElement("line", line.id);
                  const lineStations = stations.filter((s) => s.line_id === line.id);
                  return (
                    lineMappings.some((m) =>
                      filteredMappings.some((fm) => fm.id === m.id)
                    ) ||
                    lineStations.some((station) =>
                      getMappingsForElement("station", station.id).some((m) =>
                        filteredMappings.some((fm) => fm.id === m.id)
                      )
                    )
                  );
                });

              if (!hasMatchingContent) return null;

              return (
                <div key={area.id} className="rounded-lg border bg-background">
                  <button
                    type="button"
                    onClick={() => toggleArea(area.id)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                  >
                    <div
                      className="h-8 w-1 rounded-full flex-shrink-0"
                      style={{ backgroundColor: area.color || "var(--primary)" }}
                    />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-sm">{area.name}</span>
                      {area.description && (
                        <p className="text-xs text-muted-foreground truncate">
                          {area.description}
                        </p>
                      )}
                    </div>
                    {isAreaExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    )}
                  </button>

                  {isAreaExpanded && (
                    <div className="border-t px-4 pb-4 pt-3 space-y-3">
                      {/* Area-level mappings */}
                      {areaMappings.length > 0 && (
                        <div className="pl-4 border-l-2 border-muted">
                          <p className="text-xs font-medium text-muted-foreground mb-2">
                            Area datapoints
                          </p>
                          {renderMappingGroup(areaMappings, "area datapoints")}
                        </div>
                      )}

                      {/* Lines */}
                      {areaLines.map((line) => {
                        const lineStations = stations.filter(
                          (s) => s.line_id === line.id
                        );
                        const isLineExpanded = expandedLines.has(line.id);
                        const lineMappings = getMappingsForElement(
                          "line",
                          line.id
                        ).filter(
                          (m) =>
                            !searchQuery ||
                            filteredMappings.some((fm) => fm.id === m.id)
                        );

                        // Check if line has matching content
                        const lineHasMatchingContent =
                          !searchQuery ||
                          lineMappings.length > 0 ||
                          lineStations.some((station) =>
                            getMappingsForElement("station", station.id).some(
                              (m) => filteredMappings.some((fm) => fm.id === m.id)
                            )
                          );

                        if (!lineHasMatchingContent) return null;

                        return (
                          <div
                            key={line.id}
                            className="rounded-lg border bg-muted/20"
                          >
                            <button
                              type="button"
                              onClick={() => toggleLine(line.id)}
                              className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                            >
                              <GitBranch className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium">
                                  {line.name}
                                </span>
                                <Badge variant="outline" className="ml-2 text-xs">
                                  {lineStations.length} station
                                  {lineStations.length !== 1 ? "s" : ""}
                                </Badge>
                              </div>
                              {isLineExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              )}
                            </button>

                            {isLineExpanded && (
                              <div className="border-t px-4 pb-4 pt-3 space-y-3">
                                {/* Line-level mappings */}
                                {lineMappings.length > 0 && (
                                  <div className="pl-4 border-l-2 border-muted">
                                    <p className="text-xs font-medium text-muted-foreground mb-2">
                                      Line datapoints
                                    </p>
                                    {renderMappingGroup(
                                      lineMappings,
                                      "line datapoints"
                                    )}
                                  </div>
                                )}

                                {/* Stations */}
                                {lineStations.map((station) => {
                                  const stationMappings = getMappingsForElement(
                                    "station",
                                    station.id
                                  ).filter(
                                    (m) =>
                                      !searchQuery ||
                                      filteredMappings.some((fm) => fm.id === m.id)
                                  );

                                  if (
                                    searchQuery &&
                                    stationMappings.length === 0
                                  ) {
                                    return null;
                                  }

                                  return (
                                    <div
                                      key={station.id}
                                      className="rounded-lg border bg-background"
                                    >
                                      <div className="flex items-center gap-3 px-4 py-3">
                                        <Cpu className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <span className="text-sm font-medium">
                                            {station.name}
                                          </span>
                                          {station.description && (
                                            <p className="text-xs text-muted-foreground truncate">
                                              {station.description}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                      <div className="border-t px-4 py-3">
                                        {renderMappingGroup(
                                          stationMappings,
                                          "station datapoints"
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export default UNSBrowser;
